/**
 * @fileoverview System-web runtime/build-time configuration resolution.
 * @module system-web/config
 *
 * Responsibilities:
 * - Resolve web config from Docker runtime (window.__RUNTIME_CONFIG__), the
 *   /api/web-config Key Vault endpoint, and Vite build-time env (in that precedence).
 * - Expose the Application Insights connection string for browser telemetry.
 * Dependencies:
 * - window.__RUNTIME_CONFIG__ (Docker entrypoint), import.meta.env (Vite), /api/web-config.
 * Architecture:
 * - No hardcoded URLs/secrets — all values come from runtime/env; fetchWebConfig() is the
 *   only online boot step and is non-blocking with local-default fallback.
 * Notes:
 * - appInsightsConnectionString is empty when unconfigured; telemetry disables gracefully.
 */
import { fetchWebConfigWithRetries } from '@firesquirrel/web-config-client';

export type AppEnv = 'dev' | 'stage' | 'prod';

/**
 * @contract LocalDbMetadataConfig
 * Purpose: Server-authoritative expected local DB metadata from the web-config
 *          response. Used by the localDatabaseManager to validate _db_meta.
 * Fields:  dbId, environment, schemaVersion, appVersion, apiBaseUrl.
 * Rules:   Comes from the `local_db_metadata` MongoDB collection via the API.
 *          When absent, the frontend falls back to compiled-in constants.
 * Sync:    n/a — delivered via the web-config REST endpoint on startup.
 */
export interface LocalDbMetadataConfig {
  dbId: string;
  environment: AppEnv;
  schemaVersion: number;
  appVersion: string;
  apiBaseUrl: string;
}

/**
 * @contract WebConfig
 * Purpose: Resolved system-web runtime configuration.
 * Fields:  appEnv, tenantDomainBase, mapboxToken, tenantWebBaseDomain,
 *          appInsightsConnectionString, appInsightsPortalUrl, localDbMetadata.
 * Rules:   values resolve runtime → API → build-time env; empty string when unset.
 * Sync:    n/a (config, not a synced record).
 */
export interface WebConfig {
  appEnv: AppEnv;
  tenantDomainBase: string;
  mapboxToken: string;
  tenantWebBaseDomain: string;
  appInsightsConnectionString: string;
  /**
   * Azure Portal deep-link base for this environment's Application Insights
   * resource. Empty when unset — the System Status "Open in Application
   * Insights" button hides rather than pointing at a hardcoded URL.
   */
  appInsightsPortalUrl: string;
  /**
   * Server-authoritative expected local database metadata for system-web.
   * Populated from the `/api/web-config` response's `localDbMetadata` field
   * (backed by the `local_db_metadata` MongoDB collection). When absent (API
   * doesn't support it yet or fetch failed), the localDatabaseManager falls
   * back to compiled-in constants after a 10-second timeout.
   */
  localDbMetadata?: LocalDbMetadataConfig | null;
}

interface RuntimeConfig {
  VITE_APP_ENV?: string;
  VITE_AUTH_BYPASS?: string;
  VITE_TENANT_DOMAIN_BASE?: string;
  VITE_MAPBOX_TOKEN?: string;
  VITE_APPINSIGHTS_CONNECTION_STRING?: string;
  VITE_APPINSIGHTS_PORTAL_URL?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

function getRuntimeValue(key: keyof RuntimeConfig): string | undefined {
  const val = window.__RUNTIME_CONFIG__?.[key];
  // Ignore unresolved placeholders (e.g. "__VITE_API_BASE_URL__")
  if (val && val.startsWith('__') && val.endsWith('__')) return undefined;
  return val;
}

function resolveAppEnv(value: string | undefined): AppEnv {
  if (value === 'stage' || value === 'prod') {
    return value;
  }
  return 'dev';
}

// ---------------------------------------------------------------------------
// Local defaults — used immediately and as fallback if /api/web-config fails
// ---------------------------------------------------------------------------
const appEnv = resolveAppEnv(
  getRuntimeValue('VITE_APP_ENV') || import.meta.env.VITE_APP_ENV
);

let webConfig: WebConfig = {
  appEnv,
  tenantDomainBase: getRuntimeValue('VITE_TENANT_DOMAIN_BASE') || import.meta.env.VITE_TENANT_DOMAIN_BASE || 'firesquirrel.com',
  mapboxToken: getRuntimeValue('VITE_MAPBOX_TOKEN') || (import.meta.env.VITE_MAPBOX_TOKEN as string) || '',
  tenantWebBaseDomain: import.meta.env.VITE_TENANT_WEB_BASE_DOMAIN || '',
  appInsightsConnectionString:
    getRuntimeValue('VITE_APPINSIGHTS_CONNECTION_STRING') ||
    (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as string) ||
    '',
  appInsightsPortalUrl:
    getRuntimeValue('VITE_APPINSIGHTS_PORTAL_URL') ||
    (import.meta.env.VITE_APPINSIGHTS_PORTAL_URL as string) ||
    '',
};

// ---------------------------------------------------------------------------
// fetchWebConfig — call once at app startup (e.g. in main.tsx before render).
// Merges API response into the cached config. Non-blocking on failure.
// Uses the shared @firesquirrel/web-config-client fetcher with cold-start-aware
// polling: 30 attempts × 2s interval (~60s ceiling), 3s per-request timeout.
// Network errors and 5xx retry (likely cold start); 4xx fail fast (misconfig).
// ---------------------------------------------------------------------------
export async function fetchWebConfig(): Promise<WebConfig> {
  const base = import.meta.env.VITE_API_BASE_URL || '';
  // Treat Docker-internal hostnames AND localhost/127.0.0.1 as proxy-only targets —
  // the browser cannot reach them directly; requests must use relative paths so the
  // Vite dev proxy (or nginx in production) forwards them.
  const isProxyOnlyHost = /^https?:\/\/(?:localhost|127\.0\.0\.1|[^/]*(?:system-api|tenant-api|mobile-api))(?::\d+)?(?:\/|$)/i.test(base);
  const url = isProxyOnlyHost ? '/api/web-config' : `${base}/api/web-config`;
  console.log('[config] Fetching web config from:', url);

  const data = await fetchWebConfigWithRetries<Record<string, string>>(url, {
    log: (msg) => console.log(`[config] ${msg}`),
  });

  if (data) {
    // Parse localDbMetadata from the API response if present
    let localDbMetadata: LocalDbMetadataConfig | null = null;
    const rawMeta = (data as Record<string, unknown>).localDbMetadata;
    if (rawMeta && typeof rawMeta === 'object') {
      const meta = rawMeta as Record<string, unknown>;
      if (
        typeof meta.dbId === 'string' &&
        typeof meta.environment === 'string' &&
        typeof meta.schemaVersion === 'number' &&
        typeof meta.appVersion === 'string' &&
        typeof meta.apiBaseUrl === 'string'
      ) {
        localDbMetadata = {
          dbId: meta.dbId,
          environment: resolveAppEnv(meta.environment),
          schemaVersion: meta.schemaVersion,
          appVersion: meta.appVersion,
          apiBaseUrl: meta.apiBaseUrl,
        };
      }
    }

    webConfig = {
      appEnv: resolveAppEnv(data.appEnv) ?? webConfig.appEnv,
      tenantDomainBase: data.tenantDomainBase || webConfig.tenantDomainBase,
      mapboxToken: data.mapboxToken || webConfig.mapboxToken,
      tenantWebBaseDomain: data.tenantWebBaseDomain || webConfig.tenantWebBaseDomain,
      appInsightsConnectionString: data.appInsightsConnectionString || webConfig.appInsightsConnectionString,
      appInsightsPortalUrl: data.appInsightsPortalUrl || webConfig.appInsightsPortalUrl,
      localDbMetadata,
    };
    console.log('[config] Web config updated successfully');
  } else {
    console.warn('[config] /api/web-config unreachable after retries — using local defaults');
  }
  return webConfig;
}

export function getWebConfig(): WebConfig {
  return webConfig;
}

export function getTenantBaseUrl(tenantSlug: string): string {
  return `https://${tenantSlug}.${webConfig.tenantDomainBase}`;
}

/**
 * Returns true when the app should run in demo mode:
 * - Always on demo. subdomain (dev, stage, or prod)
 * - When ?demomode=true is present in the URL
 */
export function isDemoMode(): boolean {
  try {
    const host = window.location.hostname;
    if (host.startsWith('demo.') || host === 'demo') return true;
    if (new URLSearchParams(window.location.search).get('demomode') === 'true') return true;
  } catch { /* ignore */ }
  return false;
}
